import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { AxisBadge } from "../components/AxisBadge";
import { Button, StatusBadge, statusLabel, EmptyState } from "../ui";

interface ClaimSummary {
  id: string;
  claim_key: string;
  claim_label: string;
  mechanism_label: string | null;
  pillar: string;
  axis: string | null;
  regulatory_caveats: string | null;
  allowed_evidence_types: string[];
  abstract_aspect: string | null;
  status: string;
  reviewed_by: string | null;
}

interface ResolvedProof {
  type: string;
  source_hash: string;
  sub_id: string;
  note: string | null;
  chunk_text: string | null;
  chunk_page: number | null;
  table_html: string | null;
  table_caption: string | null;
  table_summary: string | null;
  figure_caption: string | null;
  figure_image_url: string | null;
  figure_page: number | null;
  resolution_error: string | null;
}

interface ResolvedClaim {
  claim_id: string;
  claim_key: string;
  claim_label: string;
  mechanism_label: string | null;
  pillar: string;
  axis: string | null;
  regulatory_caveats: string | null;
  status: string;
  allowed_evidence_types: string[];
  proofs: ResolvedProof[];
}

const PILLARS = [
  "Barrier", "Hydration", "Soothing", "Exposome",
  "Longevity", "SecondaryUV", "Neuro", "Cross",
];

// Roh-Status → deutsches Anzeige-Label. Leerer Key = "Alle" (Filter-Reset).
const CLAIM_STATUS_DE: Record<string, string> = {
  "": "Alle",
  pending: "Ausstehend",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
};

export default function ClaimReviewPage() {
  const [claims, setClaims] = useState<ClaimSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [pillarFilter, setPillarFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, ResolvedClaim | "loading" | undefined>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status_filter", statusFilter);
      if (pillarFilter) params.set("pillar", pillarFilter);
      params.set("limit", "300");
      const rows = await api.get<ClaimSummary[]>(`/api/claims?${params}`);
      setClaims(rows);
    } catch (e) {
      console.error("load claims failed", e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pillarFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const x of claims) c[x.status] = (c[x.status] ?? 0) + 1;
    return c;
  }, [claims]);

  const toggle = async (id: string) => {
    setExpanded((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: "loading" };
    });
    if (!expanded[id]) {
      try {
        const detail = await api.get<ResolvedClaim>(`/api/claims/${id}`);
        setExpanded((prev) => ({ ...prev, [id]: detail }));
      } catch (e) {
        console.error("get claim detail failed", e);
        setExpanded((prev) => {
          const { [id]: _, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const act = async (id: string, action: "approve" | "reject") => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const updated = await api.post<ClaimSummary>(`/api/claims/${id}/${action}`);
      setClaims((rows) => rows.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      console.error(`${action} failed`, e);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-ink">
            Claim-Freigabe
          </h1>
          <p className="text-sm text-ink-50/70 mt-1">
            Nur freigegebene Claims werden für Artikel und Videos verwendet.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700"
          title="Refresh"
        >
          <RefreshCcw className={clsx("w-4 h-4", loading && "animate-spin")} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs uppercase tracking-wider text-ink-50/50">Status</span>
        {["pending", "approved", "rejected", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={clsx(
              "px-3 py-1 rounded-full text-xs border transition-colors",
              statusFilter === s
                ? "bg-brand-500 text-white border-brand-500"
                : "bg-cream-50 dark:bg-ink-800 border-cream-200 dark:border-ink-600 text-ink-50 hover:bg-cream-100"
            )}
          >
            {CLAIM_STATUS_DE[s] ?? "Alle"}
            {s && counts[s] != null && (
              <span className="ml-1 opacity-70">({counts[s]})</span>
            )}
          </button>
        ))}
        <span className="ml-4 text-xs uppercase tracking-wider text-ink-50/50">Säule</span>
        <select
          value={pillarFilter}
          onChange={(e) => setPillarFilter(e.target.value)}
          aria-label="Pillar-Filter"
          className="px-2 py-1 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
        >
          <option value="">Alle Säulen</option>
          {PILLARS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span className="text-xs text-ink-50/50 ml-auto">{claims.length} Claims</span>
      </div>

      <div className="space-y-2">
        {claims.map((c) => {
          const exp = expanded[c.id];
          const isLoading = exp === "loading";
          const detail = exp && exp !== "loading" ? exp : null;
          return (
            <div
              key={c.id}
              className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg overflow-hidden"
            >
              <div className="flex items-start gap-3 p-3">
                <button
                  onClick={() => toggle(c.id)}
                  className="mt-1 text-ink-50/60 hover:text-ink"
                >
                  {exp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={statusLabel(c.status).status}>
                      {CLAIM_STATUS_DE[c.status] ?? statusLabel(c.status).label}
                    </StatusBadge>
                    <span className="text-xs text-ink-50/60">{c.pillar}</span>
                    <AxisBadge axis={c.axis} />
                    <span className="text-xs text-ink-50/40 font-mono truncate">{c.claim_key}</span>
                  </div>
                  {c.abstract_aspect && (
                    <div className="text-xs font-semibold text-brand-700 dark:text-brand-300 mb-0.5 flex items-center gap-1.5">
                      <span className="inline-block w-1 h-1 rounded-full bg-brand-500" />
                      {c.abstract_aspect}
                    </div>
                  )}
                  <div className="text-sm text-ink">{c.claim_label}</div>
                  {c.mechanism_label && (
                    <div className="text-xs text-ink-50/70 mt-1">
                      <span className="font-medium">Mechanismus:</span> {c.mechanism_label}
                    </div>
                  )}
                  {c.regulatory_caveats && (
                    <div className="text-xs text-warning mt-1">
                      ⚠ {c.regulatory_caveats}
                    </div>
                  )}
                  {c.reviewed_by && (
                    <div className="text-xs text-ink-50/40 mt-1">geprüft von {c.reviewed_by}</div>
                  )}
                </div>
                {c.status === "pending" && (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => act(c.id, "approve")}
                      disabled={busy[c.id]}
                      loading={busy[c.id]}
                      icon={<CheckCircle2 className="w-3 h-3" />}
                    >
                      Freigeben
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => act(c.id, "reject")}
                      disabled={busy[c.id]}
                      icon={<XCircle className="w-3 h-3" />}
                    >
                      Ablehnen
                    </Button>
                  </div>
                )}
              </div>

              {isLoading && (
                <div className="border-t border-cream-200 dark:border-ink-600 p-3 text-sm text-ink-50/60 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Belege werden geladen …
                </div>
              )}
              {detail && (
                <div className="border-t border-cream-200 dark:border-ink-600 p-3 bg-cream-100/50 dark:bg-ink-900/50">
                  <div className="text-xs uppercase tracking-wider text-ink-50/50 mb-2">
                    Belege ({detail.proofs.length})
                  </div>
                  <div className="space-y-2">
                    {detail.proofs.map((p, i) => (
                      <ProofCard key={`${p.type}-${p.source_hash}-${p.sub_id}-${i}`} proof={p} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!loading && claims.length === 0 && (
          statusFilter === "pending" ? (
            <EmptyState
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Keine ausstehenden Claims"
              hint="Auf „Alle“ wechseln zeigt auch bearbeitete."
              action={
                <Button variant="secondary" onClick={() => setStatusFilter("approved")}>
                  Freigegebene ansehen
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Keine Claims für diesen Filter"
              hint="Für die aktuelle Auswahl gibt es keine Claims."
              action={
                <Button variant="secondary" onClick={() => { setStatusFilter(""); setPillarFilter(""); }}>
                  Alle anzeigen
                </Button>
              }
            />
          )
        )}
      </div>
    </div>
  );
}

function ProofCard({ proof }: { proof: ResolvedProof }) {
  return (
    <div className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-md p-2 text-xs">
      <div className="flex items-center gap-2 mb-1">
        <span className="px-1.5 py-0.5 rounded-md bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300 text-[10px] font-medium uppercase">
          {proof.type}
        </span>
        <span className="font-mono text-ink-50/50 truncate">{proof.source_hash.slice(0, 12)}…/{proof.sub_id}</span>
        {proof.note && <span className="text-ink-50/60">— {proof.note}</span>}
      </div>
      {proof.resolution_error && (
        <div className="text-danger italic">⚠ {proof.resolution_error}</div>
      )}
      {proof.chunk_text && (
        <div className="text-ink italic">"{proof.chunk_text.slice(0, 600)}{proof.chunk_text.length > 600 ? "…" : ""}"</div>
      )}
      {proof.table_html && (
        <div className="max-h-64 overflow-auto bg-surface dark:bg-ink-700 border border-cream-200 dark:border-ink-600 rounded-md p-1 my-1">
          <div className="text-[11px] text-ink-50/50 mb-1">
            {proof.table_caption || proof.table_summary || "Tabelle"}
          </div>
          <div className="claim-table-render" dangerouslySetInnerHTML={{ __html: proof.table_html }} />
        </div>
      )}
      {proof.figure_image_url && (
        <div className="flex gap-2">
          <div className="text-ink-50/60">[Abbildung auf Seite {proof.figure_page ?? "?"}]</div>
          {proof.figure_caption && (
            <div className="text-ink italic flex-1">"{proof.figure_caption}"</div>
          )}
        </div>
      )}
    </div>
  );
}
